import { useState, useEffect } from 'react';
import { api } from '../../../lib/api';
import MediaUploader from '../../../components/admin/MediaUploader';
import PasswordChangeSection from './PasswordChangeSection';

interface SiteSettings {
  logo_url: string;
  favicon_url: string;
  site_title: string;
  phone: string;
  whatsapp: string;
  email: string;
  pdf_url: string;
  organization_name: string;
  organization_description: string;
  organization_url: string;
  organization_logo: string;
  organization_address: string;
  organization_city: string;
  organization_country: string;
  organization_postal_code: string;
  social_facebook: string;
  social_instagram: string;
  social_youtube: string;
  social_linkedin: string;
  social_snapchat: string;
  og_site_name: string;
  og_type: string;
  og_locale: string;
  google_tag_manager_id: string;
  google_analytics_id: string;
  search_console_id: string;
  facebook_pixel_id: string;
  custom_head_code: string;
  custom_body_code: string;
}

export default function SettingsManager() {
  const [activeTab, setActiveTab] = useState('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>({
    logo_url: '',
    favicon_url: '',
    site_title: 'Golden Tower',
    phone: '+971123456789',
    whatsapp: '971123456789',
    email: 'info@goldentower.com',
    pdf_url: '/catalog.pdf',
    organization_name: 'Golden Tower',
    organization_description: 'Luxury residential development in Dubai',
    organization_url: 'https://goldentower.com',
    organization_logo: '',
    organization_address: '',
    organization_city: 'Dubai',
    organization_country: 'UAE',
    organization_postal_code: '',
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    social_linkedin: '',
    social_snapchat: '',
    og_site_name: 'Golden Tower',
    og_type: 'website',
    og_locale: 'en_US',
    google_tag_manager_id: '',
    google_analytics_id: '',
    search_console_id: '',
    facebook_pixel_id: '',
    custom_head_code: '',
    custom_body_code: ''
  });

  const tabs = [
    { id: 'site', name: 'Site Settings', icon: 'ri-settings-3-line' },
    { id: 'contact', name: 'Contact Info', icon: 'ri-contacts-line' },
    { id: 'schema', name: 'Schema & SEO', icon: 'ri-code-s-slash-line' },
    { id: 'integrations', name: 'Integrations & Scripts', icon: 'ri-code-box-line' },
    { id: 'password', name: 'Change Password', icon: 'ri-lock-line' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await api.get('/settings');

      if (data && data.length > 0) {
        const settingsObj: any = {};
        data.forEach((item: any) => {
            settingsObj[item.setting_key] = item.setting_value;
        });
        setSettings({ ...settings, ...settingsObj });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      const response = await api.post('/settings', settings);
      
      if (!response.success) {
        throw new Error('Some settings could not be saved');
      }

      alert('Settings saved successfully!');
      await loadSettings();
    } catch (error) {
      console.error('Save settings error:', error);
      alert('An error occurred while saving settings.');
    } finally {
      setSaving(false);
    }
  };

  const renderSiteSettings = () => (
    <div className="space-y-6">
      {/* Logo */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-image-line mr-2"></i>
          Website Logo
        </h3>
        <div className="space-y-4">
          {settings.logo_url && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Logo:
              </label>
              <img 
                src={settings.logo_url} 
                alt="Site Logo" 
                className="h-16 object-contain"
              />
            </div>
          )}
          <MediaUploader
            type="image"
            currentUrl={settings.logo_url}
            label="Upload Logo (PNG format recommended)"
            onUploadComplete={(url) => setSettings({ ...settings, logo_url: url })}
          />
          <p className="text-sm text-gray-500">
            💡 Recommended size: 200x60px, transparent background (PNG)
          </p>
        </div>
      </div>

      {/* Favicon */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-window-line mr-2"></i>
          Favicon
        </h3>
        <div className="space-y-4">
          {settings.favicon_url && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Favicon:
              </label>
              <img 
                src={settings.favicon_url} 
                alt="Favicon" 
                className="h-8 w-8 object-contain"
              />
            </div>
          )}
          <MediaUploader
            type="image"
            currentUrl={settings.favicon_url}
            label="Upload Favicon (ICO or PNG)"
            onUploadComplete={(url) => setSettings({ ...settings, favicon_url: url })}
          />
          <p className="text-sm text-gray-500">
            💡 Recommended size: 32x32px or 64x64px
          </p>
        </div>
      </div>

      {/* Site Title */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-text mr-2"></i>
          Site Title
        </h3>
        <input
          type="text"
          value={settings.site_title}
          onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
          placeholder="e.g. Golden Tower"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
        />
        <p className="text-sm text-gray-500 mt-2">
          💡 Used in browser tabs and default SEO
        </p>
      </div>
    </div>
  );

  const renderContactSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-customer-service-2-line mr-2"></i>
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-phone-fill mr-2"></i>Phone Number
            </label>
            <input
              type="text"
              value={settings.phone}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              placeholder="+971123456789"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-whatsapp-fill mr-2"></i>WhatsApp Number
            </label>
            <input
              type="text"
              value={settings.whatsapp}
              onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
              placeholder="971123456789"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-sm text-gray-500 mt-1">
              💡 Include country code without '+' sign
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-mail-fill mr-2"></i>Email Address
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              placeholder="info@goldentower.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-file-pdf-fill mr-2"></i>PDF Catalog URL
            </label>
            <input
              type="text"
              value={settings.pdf_url}
              onChange={(e) => setSettings({ ...settings, pdf_url: e.target.value })}
              placeholder="/catalog.pdf"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Usage Information</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• These details are used in floating contact icons and buttons</li>
                <li>• Phone: Initiates call on click</li>
                <li>• WhatsApp: Opens WhatsApp chat on click</li>
                <li>• Email: Opens email client on click</li>
                <li>• PDF: Triggers catalog download on click</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchemaSettings = () => (
    <div className="space-y-6">
      {/* Organization Schema */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-building-line mr-2"></i>
          Organization Schema & Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name *
            </label>
            <input
              type="text"
              value={settings.organization_name}
              onChange={(e) => setSettings({ ...settings, organization_name: e.target.value })}
              placeholder="e.g. Golden Tower"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Description *
            </label>
            <textarea
              value={settings.organization_description}
              onChange={(e) => setSettings({ ...settings, organization_description: e.target.value })}
              placeholder="e.g. Leading luxury real estate residence in Accra"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Website URL *
            </label>
            <input
              type="url"
              value={settings.organization_url}
              onChange={(e) => setSettings({ ...settings, organization_url: e.target.value })}
              placeholder="https://goldentower.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Logo
            </label>
            {settings.organization_logo && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <img 
                  src={settings.organization_logo} 
                  alt="Organization Logo" 
                  className="h-16 object-contain"
                />
              </div>
            )}
            <MediaUploader
              type="image"
              currentUrl={settings.organization_logo}
              label="Upload Logo"
              onUploadComplete={(url) => setSettings({ ...settings, organization_logo: url })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={settings.organization_address}
                onChange={(e) => setSettings({ ...settings, organization_address: e.target.value })}
                placeholder="e.g. Airport Residential Area"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={settings.organization_city}
                onChange={(e) => setSettings({ ...settings, organization_city: e.target.value })}
                placeholder="Accra"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                type="text"
                value={settings.organization_country}
                onChange={(e) => setSettings({ ...settings, organization_country: e.target.value })}
                placeholder="Ghana"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                value={settings.organization_postal_code}
                onChange={(e) => setSettings({ ...settings, organization_postal_code: e.target.value })}
                placeholder="00000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Social Media */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-share-line mr-2"></i>
          Social Media Accounts
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-facebook-fill mr-2"></i>Facebook
            </label>
            <input
              type="url"
              value={settings.social_facebook}
              onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
              placeholder="https://facebook.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-instagram-line mr-2"></i>Instagram
            </label>
            <input
              type="url"
              value={settings.social_instagram}
              onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
              placeholder="https://instagram.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-youtube-fill mr-2"></i>YouTube
            </label>
            <input
              type="url"
              value={settings.social_youtube}
              onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
              placeholder="https://youtube.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-linkedin-fill mr-2"></i>LinkedIn
            </label>
            <input
              type="url"
              value={settings.social_linkedin}
              onChange={(e) => setSettings({ ...settings, social_linkedin: e.target.value })}
              placeholder="https://linkedin.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-twitter-fill mr-2"></i>X (Twitter)
            </label>
            <input
              type="url"
              value={settings.social_snapchat}
              onChange={(e) => setSettings({ ...settings, social_snapchat: e.target.value })}
              placeholder="https://x.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Open Graph */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-share-box-line mr-2"></i>
          Open Graph Settings
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Name (og:site_name)
            </label>
            <input
              type="text"
              value={settings.og_site_name}
              onChange={(e) => setSettings({ ...settings, og_site_name: e.target.value })}
              placeholder="Golden Tower"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Type (og:type)
            </label>
            <select
              value={settings.og_type}
              onChange={(e) => setSettings({ ...settings, og_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white pr-8"
            >
              <option value="website">Website</option>
              <option value="business.business">Business</option>
              <option value="place">Place</option>
              <option value="article">Article</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Locale (og:locale)
            </label>
            <select
              value={settings.og_locale}
              onChange={(e) => setSettings({ ...settings, og_locale: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white pr-8"
            >
              <option value="en_US">English (US)</option>
              <option value="en_GB">English (UK)</option>
              <option value="ar_AE">Arabic (UAE)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
          <div>
            <h4 className="font-medium text-blue-900 mb-2">About Schema.org and Open Graph</h4>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• <strong>Organization Schema:</strong> Helps search engines understand your brand profile correctly</li>
              <li>• <strong>Open Graph:</strong> Controls how your site appears when shared on social media and chat apps</li>
              <li>• Completing all fields enhances search indexing and rich preview results</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      {/* Analytics & Pixel Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 font-sans">
          <i className="ri-line-chart-line mr-2 text-[#C5A46D]"></i>
          Analytics & Tracking Codes
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Tag Manager (GTM) ID
            </label>
            <input
              type="text"
              value={settings.google_tag_manager_id || ''}
              onChange={(e) => setSettings({ ...settings, google_tag_manager_id: e.target.value })}
              placeholder="e.g. GTM-XXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">Container ID from Google Tag Manager.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Analytics (GA4) Measurement ID
            </label>
            <input
              type="text"
              value={settings.google_analytics_id || ''}
              onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
              placeholder="e.g. G-XXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">Measurement ID starting with G- from Google Analytics 4.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Search Console Verification Code
            </label>
            <input
              type="text"
              value={settings.search_console_id || ''}
              onChange={(e) => setSettings({ ...settings, search_console_id: e.target.value })}
              placeholder="e.g. google-site-verification..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">HTML meta tag verification code or token.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facebook (Meta) Pixel ID
            </label>
            <input
              type="text"
              value={settings.facebook_pixel_id || ''}
              onChange={(e) => setSettings({ ...settings, facebook_pixel_id: e.target.value })}
              placeholder="e.g. 1234567890"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">Pixel ID from Facebook Events Manager.</p>
          </div>
        </div>
      </div>

      {/* Custom Script injection fields */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 font-sans">
          <i className="ri-code-box-line mr-2 text-[#C5A46D]"></i>
          Custom HTML / JavaScript Code
        </h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between">
              <span>Head Section Custom Code (injected into &lt;head&gt;)</span>
              <span className="text-xs text-red-500 font-normal">Include &lt;script&gt; or &lt;style&gt; tags</span>
            </label>
            <textarea
              value={settings.custom_head_code || ''}
              onChange={(e) => setSettings({ ...settings, custom_head_code: e.target.value })}
              placeholder="e.g. <script src='...'></script> or <style>...</style>"
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between">
              <span>Body Section Custom Code (injected at start of &lt;body&gt;)</span>
              <span className="text-xs text-red-500 font-normal">Include &lt;script&gt; or HTML tags</span>
            </label>
            <textarea
              value={settings.custom_body_code || ''}
              onChange={(e) => setSettings({ ...settings, custom_body_code: e.target.value })}
              placeholder="e.g. <script>console.log('loaded');</script> or <div>...</div>"
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white font-mono text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Site Settings</h2>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center whitespace-nowrap cursor-pointer"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line mr-2"></i>
              Save All Settings
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium transition-colors flex items-center whitespace-nowrap cursor-pointer ${
              activeTab === tab.id
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className={`${tab.icon} mr-2`}></i>
            {tab.name}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'site' && renderSiteSettings()}
        {activeTab === 'contact' && renderContactSettings()}
        {activeTab === 'schema' && renderSchemaSettings()}
        {activeTab === 'integrations' && renderIntegrationSettings()}
        {activeTab === 'password' && <PasswordChangeSection />}
      </div>
    </div>
  );
}
